"use client";

import { useMemo, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { prepareAvatar } from "@/lib/account/prepare-avatar";
import { createClient } from "@/lib/supabase";
import { GoogleSignInButton } from "@/app/components/GoogleSignInButton";
import { trackEvent, trackGA4Event } from "@/lib/analytics";
import { trackServerEvent } from "@/app/actions/analytics";
import {
  markPendingSignupUsed,
  upsertPendingSignup,
} from "@/app/actions/pending-signup";
import { recordSignupMarketingConsent } from "@/app/actions/account-settings";
import { CascadingTree } from "@/app/components/CascadingTree";
import type { CampaignAttribution } from "@/lib/campaign-attribution";
import { getSignupCampaignAttribution } from "@/lib/campaign-attribution-client";
import {
  buildCandidateReferralCode,
  buildCreateProfilePayload,
  type CreateProfileAvailability,
} from "@/lib/account/profile-payload";
import { getAvailableSeasons, type SeasonPreset } from "@/lib/seasons";
import {
  LOCATIONS_TREE,
  NATIONALITIES,
  RIGHT_TO_WORK_REGIONS,
  ROLES_TREE,
  type RightToWorkEntry,
} from "@/lib/taxonomy";
import {
  buildSignupCallbackUrl,
  resolveSignupCompletionDestination,
  type SignupEntryIntent,
} from "@/lib/signup-next";
import {
  getAvailabilityDraftError,
  getPendingAvailabilityError,
} from "./signup-flow";

const INPUT_CLASS =
  "min-h-12 w-full rounded-[12px] border-[1.5px] border-border bg-warm-bg px-4 py-3 text-[15px] text-charcoal outline-none transition focus:border-gold focus:bg-white focus:shadow-[0_0_0_3px_rgba(196,151,90,0.12)]";
const SELECT_CLASS = `${INPUT_CLASS} appearance-none`;

type SignupFormProps = {
  refCode?: string;
  completeProfile?: boolean;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
  nextPath?: string | null;
  entryIntent: SignupEntryIntent;
  /** First-party page or CTA that sent the visitor (sanitised slug). */
  entrySource?: string | null;
  campaignAttribution?: CampaignAttribution | null;
};

type CoreDetails = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  ageConfirmed: boolean;
  /** Marketing opt-in: unticked by default, never required. */
  marketingUpdates: boolean;
};

type WorkDetails = {
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  nationality: string;
  rightToWork: RightToWorkEntry[];
  currentLocation: string;
  availabilities: CreateProfileAvailability[];
};

const DOB_OPTIONS = {
  days: Array.from({ length: 31 }, (_, index) => String(index + 1)),
  months: [
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Mar" },
    { value: "4", label: "Apr" },
    { value: "5", label: "May" },
    { value: "6", label: "Jun" },
    { value: "7", label: "Jul" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ],
  years: Array.from(
    { length: 65 },
    (_, index) => String(new Date().getFullYear() - 16 - index),
  ),
};

function destinationLabel(intent: SignupEntryIntent): string {
  if (intent === "companion") return "Continue to Companion";
  if (intent === "work") return "Continue to work setup";
  return "Create free account";
}

function birthDateFromWorkDetails(work: WorkDetails): string | null {
  if (!work.dobDay || !work.dobMonth || !work.dobYear) return null;
  const month = work.dobMonth.padStart(2, "0");
  const day = work.dobDay.padStart(2, "0");
  return `${work.dobYear}-${month}-${day}`;
}

function validBirthDate(work: WorkDetails): boolean {
  const value = birthDateFromWorkDetails(work);
  if (!value) {
    return !work.dobDay && !work.dobMonth && !work.dobYear;
  }

  const parsed = new Date(`${value}T00:00:00Z`);
  const latestAllowed = new Date();
  latestAllowed.setUTCHours(0, 0, 0, 0);
  latestAllowed.setUTCFullYear(latestAllowed.getUTCFullYear() - 16);
  return (
    Number.isFinite(parsed.getTime()) &&
    parsed.getUTCFullYear() === Number(work.dobYear) &&
    parsed.getUTCMonth() + 1 === Number(work.dobMonth) &&
    parsed.getUTCDate() === Number(work.dobDay) &&
    parsed <= latestAllowed
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("The selected photo could not be read."));
    reader.onerror = () =>
      reject(new Error("The selected photo could not be read."));
    reader.readAsDataURL(file);
  });
}

async function recordReferral(input: {
  userId: string;
  referralCode: string | null;
}): Promise<void> {
  if (!input.referralCode) return;
  const supabase = createClient();
  const { data: ambassadorCode } = await supabase
    .from("referral_codes")
    .select("code")
    .eq("code", input.referralCode)
    .maybeSingle();

  if (ambassadorCode) {
    await supabase
      .from("profiles")
      .update({ referred_by_code: input.referralCode })
      .eq("user_id", input.userId);
    return;
  }

  const { data: referrer } = await supabase
    .from("profiles")
    .select("user_id")
    .eq("referral_code", input.referralCode)
    .maybeSingle();
  if (referrer?.user_id) {
    await supabase.from("referrals").insert({
      referrer_id: referrer.user_id,
      referred_id: input.userId,
      referral_code: input.referralCode,
    });
  }
}

export function SignupForm({
  refCode,
  completeProfile = false,
  initialEmail = "",
  initialFirstName = "",
  initialLastName = "",
  nextPath = null,
  entryIntent,
  entrySource = null,
  campaignAttribution,
}: SignupFormProps) {
  const seasonGroups = useMemo(() => getAvailableSeasons(), []);
  const seasonPresets = useMemo(
    () => seasonGroups.flatMap((group) => group.presets),
    [seasonGroups],
  );
  const [step, setStep] = useState<"core" | "work">("core");
  const [details, setDetails] = useState<CoreDetails>({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
    password: "",
    ageConfirmed: false,
    marketingUpdates: false,
  });
  const [work, setWork] = useState<WorkDetails>({
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    nationality: "",
    rightToWork: [],
    currentLocation: "",
    availabilities: [],
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<SeasonPreset | null>(
    null,
  );
  const [availabilityRoles, setAvailabilityRoles] = useState<Set<string>>(
    new Set(),
  );
  const [availabilityLocations, setAvailabilityLocations] = useState<
    Set<string>
  >(new Set());
  const [datesFlexible, setDatesFlexible] = useState(true);
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableTo, setAvailableTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailResent, setEmailResent] = useState(false);
  const submitting = useRef(false);
  const signupStarted = useRef(false);
  function markSignupStarted() {
    if (signupStarted.current) return;
    signupStarted.current = trackEvent("signup_started", {
      entry_intent: entryIntent,
      ...(entrySource ? { entry_source: entrySource } : {}),
      ...(getSignupCampaignAttribution(campaignAttribution) ?? {}),
    });
  }
  const photoInput = useRef<HTMLInputElement>(null);

  const update = <K extends keyof CoreDetails>(
    field: K,
    value: CoreDetails[K],
  ) => {
    setDetails((current) => ({ ...current, [field]: value }));
  };

  const updateWork = <K extends keyof WorkDetails>(
    field: K,
    value: WorkDetails[K],
  ) => {
    setWork((current) => ({ ...current, [field]: value }));
  };

  function validateCore(): string | null {
    if (!details.firstName.trim() || !details.lastName.trim()) {
      return "Enter your first and last name.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email.trim())) {
      return "Enter a valid email address.";
    }
    if (!completeProfile && details.password.length < 8) {
      return "Use at least 8 characters for your password.";
    }
    if (!details.ageConfirmed) {
      return "Confirm that you are at least 16 years old.";
    }
    return null;
  }

  function validateOptionalWorkDetails(): string | null {
    if (!validBirthDate(work)) {
      return "Complete all three date of birth fields with a valid date, or leave them blank.";
    }
    return getPendingAvailabilityError({
      hasSelectedSeason: selectedSeason !== null,
      roleCount: availabilityRoles.size,
      availableFrom,
      availableTo,
    });
  }

  function buildSignupData(skipWork: boolean): Record<string, unknown> {
    const birthDate = skipWork ? null : birthDateFromWorkDetails(work);
    return {
      firstName: details.firstName.trim(),
      lastName: details.lastName.trim(),
      email: details.email.trim().toLowerCase(),
      birthDate,
      nationality: skipWork ? null : work.nationality,
      rightToWork: skipWork ? [] : work.rightToWork,
      currentLocation: skipWork ? null : work.currentLocation,
      availabilities: skipWork ? [] : work.availabilities,
      photoDataUrl: skipWork ? null : photoPreview,
      marketingUpdates: details.marketingUpdates,
    };
  }

  async function uploadAvatar(userId: string): Promise<void> {
    if (!photoFile) return;
    const supabase = createClient();
    const path = `${userId}/avatar.jpg`;
    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(path, photoFile, {
        contentType: "image/jpeg",
        upsert: true,
      });
    if (uploadError) return;

    const { data: publicData } = supabase.storage
      .from("avatars")
      .getPublicUrl(path);
    await supabase
      .from("profiles")
      .update({ photo_url: publicData.publicUrl })
      .eq("user_id", userId);
  }

  async function createProfile(
    userId: string,
    signupData: Record<string, unknown>,
    includePhoto: boolean,
    authEmail?: string | null,
  ) {
    const supabase = createClient();
    const profileData = buildCreateProfilePayload({
      userId,
      authEmail,
      referralCode: buildCandidateReferralCode(
        details.firstName,
        details.lastName,
      ),
      signupData,
    });
    const { error: profileError } = await supabase.rpc("create_profile", {
      profile_data: profileData,
    });

    if (profileError) {
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();
      if (!existing) throw profileError;
    }

    if (includePhoto) {
      await uploadAvatar(userId);
    }
    if (details.marketingUpdates) {
      try {
        await recordSignupMarketingConsent();
      } catch {
        // Non-blocking: the account exists and the choice can be made in Settings.
      }
    }
    await recordReferral({
      userId,
      referralCode: refCode ?? null,
    });
  }

  async function finish(
    userId: string,
    method: string,
    hasAvailabilityWindows: boolean,
  ) {
    await trackServerEvent({
      event: "signup_completed",
      properties: {
        has_referral: Boolean(refCode),
        has_availability_windows: hasAvailabilityWindows,
        signup_method: method,
        entry_intent: entryIntent,
        ...(entrySource ? { entry_source: entrySource } : {}),
        ...(getSignupCampaignAttribution(campaignAttribution) ?? {}),
      },
    });
    trackGA4Event("sign_up", { method: "candidate" });
    window.location.href = resolveSignupCompletionDestination({
      hasAvailabilityWindows,
      next: nextPath,
      entryIntent,
    });
  }

  async function createAccount(skipWork: boolean) {
    if (submitting.current) return;

    const coreError = validateCore();
    const workError = skipWork ? null : validateOptionalWorkDetails();
    if (coreError || workError) {
      setError(coreError ?? workError);
      return;
    }

    submitting.current = true;
    setLoading(true);
    setError(null);

    const signupData = buildSignupData(skipWork);
    const hasAvailabilityWindows =
      !skipWork && work.availabilities.length > 0;

    try {
      const supabase = createClient();

      if (completeProfile) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Your session has expired. Log in again.");
        await createProfile(user.id, signupData, !skipWork, user.email);
        await finish(
          user.id,
          "signup_form_existing_session",
          hasAvailabilityWindows,
        );
        return;
      }

      const pendingData: Record<string, unknown> = {
        version: 5,
        createdAt: new Date().toISOString(),
        next: nextPath,
        entryIntent,
        entrySource,
        refCode: refCode ?? null,
        campaignAttribution: getSignupCampaignAttribution(campaignAttribution),
        ageConfirmed: true,
        ...signupData,
      };
      const { error: pendingError } = await upsertPendingSignup(
        details.email,
        pendingData,
      );
      if (pendingError) {
        throw new Error("We could not save your signup. Please try again.");
      }

      const signupStageProperties = {
        confirmation_channel: "email",
        has_availability_windows: hasAvailabilityWindows,
        has_referral: Boolean(refCode),
        signup_method: "signup_form",
        entry_intent: entryIntent,
        ...(entrySource ? { entry_source: entrySource } : {}),
        ...(getSignupCampaignAttribution(campaignAttribution) ?? {}),
      };
      trackEvent("signup_details_submitted", signupStageProperties);

      const { data: signUpData, error: authError } =
        await supabase.auth.signUp({
          email: details.email.trim(),
          password: details.password,
          options: {
            emailRedirectTo: buildSignupCallbackUrl(
              window.location.origin,
              nextPath,
            ),
          },
        });
      if (authError) throw authError;
      if (signUpData?.user?.identities?.length === 0) {
        throw new Error(
          "An account with this email already exists. Try logging in.",
        );
      }
      trackEvent("signup_confirmation_pending", signupStageProperties);

      if (signUpData.session?.user) {
        await createProfile(
          signUpData.session.user.id,
          signupData,
          !skipWork,
          signUpData.session.user.email,
        );
        await markPendingSignupUsed(details.email);
        await finish(
          signUpData.session.user.id,
          "signup_form_immediate_session",
          hasAvailabilityWindows,
        );
        return;
      }

      setAwaitingConfirmation(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (entryIntent === "work" && step === "core") {
      const validationError = validateCore();
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      setStep("work");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    await createAccount(false);
  }

  async function handlePhoto(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Choose a JPG, PNG or WebP photo.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Choose a photo smaller than 8 MB.");
      return;
    }
    try {
      const resized = await prepareAvatar(file);
      const preview = await readAsDataUrl(resized);
      setPhotoFile(resized);
      setPhotoPreview(preview);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The selected photo could not be read.",
      );
    }
  }

  function chooseSeason(value: string) {
    const preset = seasonPresets.find((entry) => entry.label === value) ?? null;
    setSelectedSeason(preset);
    setAvailabilityRoles(new Set());
    setAvailabilityLocations(new Set());
    setDatesFlexible(true);
    setAvailableFrom(preset?.from ?? "");
    setAvailableTo(preset?.to ?? "");
  }

  function addAvailability() {
    const draftError = getAvailabilityDraftError({
      hasSelectedSeason: selectedSeason !== null,
      roleCount: availabilityRoles.size,
      availableFrom,
      availableTo,
    });
    if (draftError || !selectedSeason) {
      setError(draftError);
      return;
    }
    if (
      work.availabilities.some(
        (entry) => entry.season_label === selectedSeason.label,
      )
    ) {
      setError("You have already added that season.");
      return;
    }

    updateWork("availabilities", [
      ...work.availabilities,
      {
        season_label: selectedSeason.label,
        available_from: availableFrom,
        available_to: availableTo,
        dates_flexible: datesFlexible,
        flexible_earliest_start: null,
        flexible_latest_end: null,
        positions: Array.from(availabilityRoles),
        preferred_locations: Array.from(availabilityLocations),
      },
    ]);
    chooseSeason("");
    setError(null);
  }

  async function resendConfirmation() {
    setEmailResent(false);
    setError(null);
    const supabase = createClient();
    const { error: resendError } = await supabase.auth.resend({
      type: "signup",
      email: details.email.trim(),
      options: {
        emailRedirectTo: buildSignupCallbackUrl(
          window.location.origin,
          nextPath,
        ),
      },
    });
    if (resendError) {
      setError("We could not resend the email just now. Try again shortly.");
      return;
    }
    trackEvent("signup_confirmation_resent", {
      confirmation_channel: "email",
      signup_method: "signup_form",
    });
    setEmailResent(true);
  }

  if (awaitingConfirmation) {
    return (
      <main className="min-h-[72vh] bg-warm-bg px-5 py-12 md:py-20">
        <section className="mx-auto max-w-[560px] rounded-[28px] border border-border-light bg-white px-6 py-10 text-center shadow-[0_20px_60px_rgba(15,31,45,0.08)] sm:px-12">
          <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-full bg-navy text-white">
            <svg
              aria-hidden="true"
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
            >
              <rect x="2.5" y="5" width="19" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
          </div>
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[2.5px] text-gold">
            One last step
          </p>
          <h1 className="font-serif text-[36px] text-navy sm:text-[44px]">
            Check your inbox
          </h1>
          <p className="mt-4 text-[15px] leading-7 text-text-muted">
            We sent a confirmation link to{" "}
            <strong className="text-charcoal">{details.email}</strong>. It may
            take a minute, and can land in Spam or Promotions.
          </p>
          {error ? (
            <p className="mt-5 rounded-[10px] bg-error/10 px-4 py-3 text-sm text-error">
              {error}
            </p>
          ) : null}
          {emailResent ? (
            <p className="mt-5 text-sm font-semibold text-success">
              Confirmation email resent.
            </p>
          ) : null}
          <button
            type="button"
            onClick={resendConfirmation}
            className="mt-7 min-h-11 rounded-sm bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-gold"
          >
            Resend email
          </button>
          <button
            type="button"
            onClick={() => {
              setAwaitingConfirmation(false);
              setStep("core");
            }}
            className="mx-auto mt-5 block min-h-11 bg-transparent px-3 text-sm font-semibold text-text-muted underline hover:text-charcoal"
          >
            Change email address
          </button>
        </section>
      </main>
    );
  }

  const isWorkStep = entryIntent === "work" && step === "work";

  return (
    <main className="bg-warm-bg px-5 pb-20 pt-8 sm:pt-12">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-8 text-center">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[2.5px] text-gold">
            {isWorkStep ? "Optional work setup" : "Free PeakWave account"}
          </p>
          <h1 className="font-serif text-[38px] leading-tight text-navy sm:text-[48px]">
            {isWorkStep
              ? "Set up your work profile"
              : "One account for every adventure"}
          </h1>
          <p className="mx-auto mt-4 max-w-[570px] text-[15px] leading-7 text-text-muted">
            {isWorkStep
              ? "Add what employers need to find you. Every field can also be completed later from Profile."
              : "Start with the essentials. Companion and work setup stay optional, and you can add either whenever it becomes useful."}
          </p>
          {entryIntent === "work" ? (
            <div className="mx-auto mt-6 grid max-w-[360px] grid-cols-2 gap-2 text-left text-xs font-semibold">
              <span
                className={`rounded-full px-3 py-2 ${
                  step === "core"
                    ? "bg-navy text-white"
                    : "bg-navy/10 text-navy"
                }`}
              >
                {step === "work" ? "✓ " : "1. "}Account
              </span>
              <span
                className={`rounded-full px-3 py-2 ${
                  step === "work"
                    ? "bg-gold text-white"
                    : "bg-white text-text-muted"
                }`}
              >
                2. Work setup
              </span>
            </div>
          ) : null}
        </div>

        <form
          onSubmit={handleSubmit}
          onChangeCapture={markSignupStarted}
          className="overflow-hidden rounded-[28px] border border-border-light bg-white shadow-[0_22px_70px_rgba(15,31,45,0.08)]"
        >
          <div className="grid h-2 grid-cols-3">
            <span className="bg-navy" />
            <span className="bg-gold" />
            <span className="bg-terracotta" />
          </div>
          <div className="p-6 sm:p-10">
            {!isWorkStep ? (
              <>
                {!completeProfile ? (
                  <GoogleSignInButton
                    next={nextPath}
                    intent={entryIntent}
                    refCode={refCode}
                    source={entrySource ?? undefined}
                    campaignAttribution={campaignAttribution}
                    disabled={loading}
                  />
                ) : (
                  <p className="mb-6 text-sm text-text-muted">
                    You are signed in. Confirm your details below to finish setting up your account.
                  </p>
                )}
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-semibold text-charcoal">
                    First name
                    <input
                      autoComplete="given-name"
                      value={details.firstName}
                      onChange={(event) =>
                        update("firstName", event.target.value)
                      }
                      className={INPUT_CLASS}
                      required
                    />
                  </label>
                  <label className="grid gap-2 text-sm font-semibold text-charcoal">
                    Last name
                    <input
                      autoComplete="family-name"
                      value={details.lastName}
                      onChange={(event) =>
                        update("lastName", event.target.value)
                      }
                      className={INPUT_CLASS}
                      required
                    />
                  </label>
                </div>

                <label className="mt-5 grid gap-2 text-sm font-semibold text-charcoal">
                  Email address
                  <input
                    type="email"
                    autoComplete="email"
                    value={details.email}
                    onChange={(event) => update("email", event.target.value)}
                    className={INPUT_CLASS}
                    disabled={completeProfile && Boolean(initialEmail)}
                    required
                  />
                </label>

                {!completeProfile ? (
                  <label className="mt-5 grid gap-2 text-sm font-semibold text-charcoal">
                    Password
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={details.password}
                      onChange={(event) =>
                        update("password", event.target.value)
                      }
                      className={INPUT_CLASS}
                      minLength={8}
                      required
                    />
                    <span className="text-xs font-normal text-text-muted">
                      At least 8 characters.
                    </span>
                  </label>
                ) : null}

                <label className="mt-6 flex min-h-11 cursor-pointer items-start gap-3 rounded-[12px] bg-warm-bg px-4 py-3 text-sm leading-6 text-text-body">
                  <input
                    type="checkbox"
                    checked={details.ageConfirmed}
                    onChange={(event) =>
                      update("ageConfirmed", event.target.checked)
                    }
                    className="mt-1 h-4 w-4 accent-gold"
                  />
                  <span>
                    I confirm that I am at least 16 years old and accept the{" "}
                    <Link href="/terms" className="font-semibold text-navy">
                      terms
                    </Link>{" "}
                    and{" "}
                    <Link href="/privacy" className="font-semibold text-navy">
                      privacy policy
                    </Link>
                    .
                  </span>
                </label>

                <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-3 rounded-[12px] px-4 py-3 text-sm leading-6 text-text-body">
                  <input
                    type="checkbox"
                    checked={details.marketingUpdates}
                    onChange={(event) =>
                      update("marketingUpdates", event.target.checked)
                    }
                    className="mt-1 h-4 w-4 accent-teal"
                  />
                  <span>
                    <span className="font-semibold text-charcoal">
                      News and conditions updates (optional).
                    </span>{" "}
                    Email me occasional PeakWave news, new features and
                    conditions highlights. You can unsubscribe any time in
                    Settings.
                  </span>
                </label>
              </>
            ) : (
              <div className="space-y-8">
                <section>
                  <h2 className="font-serif text-2xl text-navy">
                    Your work profile
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Date of birth, nationality, a right-to-work answer and an
                    active availability window with at least one role make you
                    visible to employers. Photo and location add useful
                    context, but never gate visibility.
                  </p>
                </section>

                <section className="grid gap-5 sm:grid-cols-[140px_1fr] sm:items-center">
                  <button
                    type="button"
                    onClick={() => photoInput.current?.click()}
                    className="relative flex aspect-square w-[140px] items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gold/40 bg-warm-bg text-center text-xs font-semibold text-gold transition hover:border-gold"
                  >
                    {photoPreview ? (
                      <Image
                        src={photoPreview}
                        alt="Selected profile photo"
                        fill
                        unoptimized
                        className="object-cover"
                      />
                    ) : (
                      <span className="px-4">Add a photo</span>
                    )}
                  </button>
                  <div>
                    <h3 className="text-sm font-semibold text-charcoal">
                      Profile photo
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-text-muted">
                      Optional. A clear photo helps employers recognise you.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => photoInput.current?.click()}
                        className="min-h-11 rounded-sm border border-border bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-gold"
                      >
                        {photoPreview ? "Change photo" : "Choose photo"}
                      </button>
                      {photoPreview ? (
                        <button
                          type="button"
                          onClick={() => {
                            setPhotoFile(null);
                            setPhotoPreview(null);
                          }}
                          className="min-h-11 rounded-sm px-4 py-2 text-sm font-semibold text-text-muted hover:text-error"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <input
                      ref={photoInput}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handlePhoto}
                      className="sr-only"
                    />
                  </div>
                </section>

                <section className="grid gap-5 sm:grid-cols-2">
                  <fieldset>
                    <legend className="mb-2 text-sm font-semibold text-charcoal">
                      Date of birth
                    </legend>
                    <div className="grid grid-cols-[0.9fr_1.1fr_1.4fr] gap-2">
                      <select
                        aria-label="Date of birth day"
                        value={work.dobDay}
                        onChange={(event) =>
                          updateWork("dobDay", event.target.value)
                        }
                        className={SELECT_CLASS}
                      >
                        <option value="">Day</option>
                        {DOB_OPTIONS.days.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Date of birth month"
                        value={work.dobMonth}
                        onChange={(event) =>
                          updateWork("dobMonth", event.target.value)
                        }
                        className={SELECT_CLASS}
                      >
                        <option value="">Month</option>
                        {DOB_OPTIONS.months.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label="Date of birth year"
                        value={work.dobYear}
                        onChange={(event) =>
                          updateWork("dobYear", event.target.value)
                        }
                        className={SELECT_CLASS}
                      >
                        <option value="">Year</option>
                        {DOB_OPTIONS.years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </fieldset>

                  <label className="grid content-start gap-2 text-sm font-semibold text-charcoal">
                    Nationality
                    <select
                      value={work.nationality}
                      onChange={(event) =>
                        updateWork("nationality", event.target.value)
                      }
                      className={SELECT_CLASS}
                    >
                      <option value="">Select nationality</option>
                      {NATIONALITIES.map((nationality) => (
                        <option key={nationality} value={nationality}>
                          {nationality}
                        </option>
                      ))}
                    </select>
                  </label>
                </section>

                <fieldset>
                  <legend className="text-sm font-semibold text-charcoal">
                    Right to work
                  </legend>
                  <p className="mt-1 text-xs leading-5 text-text-muted">
                    Answer at least one region to meet employer visibility
                    requirements.
                  </p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {RIGHT_TO_WORK_REGIONS.map((region) => {
                      const current = work.rightToWork.find(
                        (entry) => entry.region === region,
                      );
                      return (
                        <label
                          key={region}
                          className="grid grid-cols-[1fr_128px] items-center gap-3 rounded-[12px] border border-border-light px-3 py-2 text-sm font-medium text-charcoal"
                        >
                          {region}
                          <select
                            aria-label={`Right to work in ${region}`}
                            value={current?.status ?? ""}
                            onChange={(event) => {
                              const remaining = work.rightToWork.filter(
                                (entry) => entry.region !== region,
                              );
                              const status = event.target.value as
                                | RightToWorkEntry["status"]
                                | "";
                              updateWork(
                                "rightToWork",
                                status
                                  ? [...remaining, { region, status }]
                                  : remaining,
                              );
                            }}
                            className="min-h-11 rounded-[10px] border border-border bg-warm-bg px-3 text-sm"
                          >
                            <option value="">Not answered</option>
                            <option value="yes">Yes</option>
                            <option value="sponsorship">
                              With visa or sponsorship
                            </option>
                          </select>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <label className="grid gap-2 text-sm font-semibold text-charcoal">
                  Current location
                  <input
                    value={work.currentLocation}
                    onChange={(event) =>
                      updateWork("currentLocation", event.target.value)
                    }
                    placeholder="For example, Innsbruck, Austria"
                    className={INPUT_CLASS}
                  />
                  <span className="text-xs font-normal text-text-muted">
                    Optional. This can be a town, resort or country.
                  </span>
                </label>

                <section className="rounded-[18px] border border-border-light bg-warm-bg/60 p-4 sm:p-6">
                  <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-gold">
                    Optional step
                  </p>
                  <h2 className="mt-2 font-serif text-2xl text-navy">
                    Seasonal availability for work
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-text-muted">
                    Add a season and at least one role to become visible to
                    employers. You can skip this and add it later.
                  </p>

                  {work.availabilities.length > 0 ? (
                    <ul className="mt-5 space-y-2">
                      {work.availabilities.map((availability) => (
                        <li
                          key={availability.season_label}
                          className="flex items-start justify-between gap-4 rounded-[12px] border border-success/20 bg-white px-4 py-3"
                        >
                          <div>
                            <p className="text-sm font-semibold text-charcoal">
                              {availability.season_label}
                            </p>
                            <p className="mt-1 text-xs text-text-muted">
                              {availability.positions.length} role
                              {availability.positions.length === 1 ? "" : "s"}{" "}
                              · {availability.available_from} to{" "}
                              {availability.available_to}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateWork(
                                "availabilities",
                                work.availabilities.filter(
                                  (entry) =>
                                    entry.season_label !==
                                    availability.season_label,
                                ),
                              )
                            }
                            className="min-h-11 px-2 text-xs font-semibold text-text-muted hover:text-error"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <label className="mt-5 grid gap-2 text-sm font-semibold text-charcoal">
                    Season
                    <select
                      value={selectedSeason?.label ?? ""}
                      onChange={(event) => chooseSeason(event.target.value)}
                      className={SELECT_CLASS}
                    >
                      <option value="">Choose a season</option>
                      {seasonGroups.map((group) => (
                        <optgroup key={group.region} label={group.region}>
                          {group.presets.map((preset) => (
                            <option
                              key={preset.label}
                              value={preset.label}
                              disabled={work.availabilities.some(
                                (entry) =>
                                  entry.season_label === preset.label,
                              )}
                            >
                              {preset.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>

                  {selectedSeason ? (
                    <div className="mt-5 space-y-5">
                      <CascadingTree
                        tree={ROLES_TREE}
                        selected={availabilityRoles}
                        onChange={setAvailabilityRoles}
                        label="Roles"
                        maxHeight="260px"
                        searchable
                      />
                      <CascadingTree
                        tree={LOCATIONS_TREE}
                        selected={availabilityLocations}
                        onChange={setAvailabilityLocations}
                        label="Preferred locations (optional)"
                        maxHeight="240px"
                        searchable
                      />
                      <label className="flex min-h-11 items-center gap-3 text-sm font-medium text-charcoal">
                        <input
                          type="checkbox"
                          checked={datesFlexible}
                          onChange={(event) =>
                            setDatesFlexible(event.target.checked)
                          }
                          className="h-5 w-5 accent-gold"
                        />
                        My dates are flexible
                      </label>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <label className="grid gap-2 text-sm font-semibold text-charcoal">
                          Available from
                          <input
                            type="date"
                            value={availableFrom}
                            onChange={(event) =>
                              setAvailableFrom(event.target.value)
                            }
                            className={INPUT_CLASS}
                          />
                        </label>
                        <label className="grid gap-2 text-sm font-semibold text-charcoal">
                          Available to
                          <input
                            type="date"
                            value={availableTo}
                            onChange={(event) =>
                              setAvailableTo(event.target.value)
                            }
                            className={INPUT_CLASS}
                          />
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={addAvailability}
                        className="min-h-11 rounded-sm border border-navy bg-white px-5 py-2.5 text-sm font-semibold text-navy transition hover:bg-navy hover:text-white"
                      >
                        Add this availability
                      </button>
                    </div>
                  ) : null}
                </section>
              </div>
            )}

            {error ? (
              <div
                role="alert"
                className="mt-5 rounded-[10px] border border-error/20 bg-error/10 px-4 py-3 text-sm font-medium text-error"
              >
                {error}
              </div>
            ) : null}

            {isWorkStep ? (
              <div className="mt-7 grid gap-3 sm:grid-cols-[1fr_auto]">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex min-h-12 items-center justify-center rounded-sm bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-gold disabled:cursor-wait disabled:opacity-60"
                >
                  {loading ? "Creating your account…" : "Create account and continue"}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => createAccount(true)}
                  className="min-h-12 rounded-sm px-5 py-3 text-sm font-semibold text-text-muted transition hover:text-navy disabled:opacity-60"
                >
                  Skip work setup
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setError(null);
                    setStep("core");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="min-h-11 text-left text-xs font-semibold text-text-muted underline sm:col-span-2"
                >
                  Back to account details
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="mt-7 flex min-h-12 w-full items-center justify-center rounded-sm bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-gold disabled:cursor-wait disabled:opacity-60"
              >
                {loading
                  ? "Creating your account…"
                  : destinationLabel(entryIntent)}
              </button>
            )}

            <p className="mt-5 text-center text-xs leading-5 text-text-muted">
              Work details are never required to create an account. PeakWave is
              free for candidates and employers.
            </p>
          </div>
        </form>

        <p className="mt-7 text-center text-sm text-text-muted">
          Already have an account?{" "}
          <Link
            href={
              nextPath
                ? `/login?redirect=${encodeURIComponent(nextPath)}`
                : "/login"
            }
            className="font-semibold text-gold"
          >
            Log in
          </Link>
        </p>
      </div>
    </main>
  );
}
